import { Controller } from "@hotwired/stimulus"

// カテゴリー（施術／物販の分類）の表示順を HTML5 ネイティブ D&D で並び替える。
// ツリー構造（ancestry）のため、以下の制約を設ける:
//   - 並び替えは「同じ親を持つ兄弟」内のみ（カテゴリーの親子移動は不可）
//   - 親カテゴリーをドラッグすると、その子孫の行もまとめて移動する
//
// 使い方:
//   <tbody data-controller="category-sortable"
//          data-category-sortable-url-value="/ja/shops/28/service_categories/reorder">
//     <tr draggable="true" data-category-sortable-target="item"
//         data-id="1" data-parent="" data-depth="0">...</tr>
//     ...
//   </tbody>
export default class extends Controller {
  static values = { url: String }
  static targets = ["item"]

  connect() {
    this.dragged = null
    this.draggedParent = null
    this.draggedBlock = []
    this.itemTargets.forEach((row) => this.bindRow(row))
  }

  bindRow(row) {
    row.addEventListener("dragstart", (e) => this.onDragStart(e, row))
    row.addEventListener("dragend", () => this.onDragEnd())
    row.addEventListener("dragover", (e) => this.onDragOver(e, row))
    row.addEventListener("drop", (e) => this.onDrop(e))
  }

  // 指定行 + それに続く「より深い」行（＝子孫）をひとかたまりとして返す
  blockOf(row) {
    const depth = parseInt(row.dataset.depth, 10)
    const block = [row]
    let node = row.nextElementSibling
    while (node && node.dataset && parseInt(node.dataset.depth, 10) > depth) {
      block.push(node)
      node = node.nextElementSibling
    }
    return block
  }

  siblingsOf(parent) {
    return this.itemTargets.filter((r) => (r.dataset.parent || "") === (parent || ""))
  }

  onDragStart(event, row) {
    this.dragged = row
    this.draggedParent = row.dataset.parent || ""
    this.draggedBlock = this.blockOf(row)
    this.draggedBlock.forEach((r) => r.classList.add("category-sortable-ghost"))
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = "move"
      event.dataTransfer.setData("text/plain", row.dataset.id || "")
    }
  }

  onDragEnd() {
    this.itemTargets.forEach((r) => {
      r.classList.remove("category-sortable-ghost", "category-sortable-drag-over")
    })
    this.dragged = null
    this.draggedParent = null
    this.draggedBlock = []
  }

  onDragOver(event, row) {
    if (!this.dragged) return
    // 同じ親の兄弟以外、または自分自身/自分の配下にはドロップ不可
    const targetParent = row.dataset.parent || ""
    if (targetParent !== this.draggedParent) return
    if (this.draggedBlock.includes(row)) return

    event.preventDefault()
    if (event.dataTransfer) event.dataTransfer.dropEffect = "move"

    const rect = row.getBoundingClientRect()
    const isAfter = (event.clientY - rect.top) > rect.height / 2
    const targetBlock = this.blockOf(row)
    const parentNode = this.dragged.parentNode

    const reference = isAfter
      ? targetBlock[targetBlock.length - 1].nextElementSibling
      : row

    this.draggedBlock.forEach((node) => parentNode.insertBefore(node, reference))
  }

  onDrop(event) {
    event.preventDefault()
    this.persist()
  }

  persist() {
    const siblings = this.siblingsOf(this.draggedParent)
    const orderedIds = siblings.map((r) => r.dataset.id)

    // 表示中の position バッジを更新（リロードせずとも見た目を整える）
    siblings.forEach((r, idx) => {
      const badge = r.querySelector(".badge")
      if (badge) badge.textContent = idx
    })

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const body = new URLSearchParams()
    orderedIds.forEach((id) => body.append("ordered_ids[]", id))

    fetch(this.urlValue, {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": token || "",
        "Content-Type": "application/x-www-form-urlencoded",
        "Accept": "application/json"
      },
      body: body.toString(),
      credentials: "same-origin"
    }).catch((err) => console.error("カテゴリーの並び替え保存に失敗しました", err))
  }
}
